import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Heading1, Type, Gauge, BarChart3, Table as TableIcon, Image as ImageIcon,
  Minus, MoveVertical, FileOutput, Trash2, Download, Clock,
  History, RefreshCw, Loader2, Copy, Mail, Share2, GripVertical, Link2, ShieldCheck,
  BookmarkPlus, MessagesSquare, LayoutGrid,
} from "lucide-react";
import { ElementSettings, ParametersEditor, PageSettings, ReportFiltersEditor } from "@/components/bi/ReportElementEditors";
import { ReportPaper, tokFor } from "@/components/bi/ReportPreview";
import { ReportCommentsPanel } from "@/components/bi/ReportCommentsPanel";
import { VersionTimeline } from "@/components/bi/VersionTimeline";
import { useAuth } from "@/context/AuthContext";

const EL_TYPES = [
  { type: "heading", label: "Heading", icon: Heading1 },
  { type: "text", label: "Text", icon: Type },
  { type: "kpi", label: "KPI row", icon: Gauge },
  { type: "chart", label: "Chart", icon: BarChart3 },
  { type: "table", label: "Table", icon: TableIcon },
  { type: "matrix", label: "Matrix", icon: LayoutGrid },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: MoveVertical },
  { type: "pagebreak", label: "Page break", icon: FileOutput },
];
const EL_DEFAULTS = {
  heading: { text: "Section heading", level: 2 },
  text: { text: "Write something…", size: 12 },
  chart: { chart_type: "column", height: 320, limit: 20 },
  table: { totals: true, subtotals: true, zebra: true, limit: 500 },
  matrix: { row_totals: true, col_totals: true, limit: 2000 },
  spacer: { height: 16 },
};
const SAVE_FIELDS = ["name", "description", "page", "header", "footer", "watermark", "theme", "parameters", "filters", "elements"];
const FMT_LABELS = [["pdf", "PDF"], ["xlsx", "Excel"], ["docx", "Word"], ["html", "HTML"]];

function ShareDialog({ open, onOpenChange, reportId }) {
  const [state, setState] = useState(null);
  const [pw, setPw] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/report-designs/${reportId}/share`).then(({ data }) => {
    setState(data);
    setExpiry(data.expires_at ? String(data.expires_at).slice(0, 10) : "");
  }).catch(() => {}), [reportId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const call = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } setBusy(false); };
  const enable = () => call(() => api.post(`/report-designs/${reportId}/share`, {}));
  const disable = () => call(() => api.delete(`/report-designs/${reportId}/share`));
  const savePw = () => call(async () => { await api.post(`/report-designs/${reportId}/share/config`, { password: pw }); setPw(""); toast.success(pw ? "Password set" : "Password cleared"); });
  const saveExpiry = () => call(async () => {
    await api.post(`/report-designs/${reportId}/share/config`, { expires_at: expiry ? new Date(`${expiry}T23:59:59Z`).toISOString() : "" });
    toast.success(expiry ? "Expiry set" : "Expiry cleared");
  });
  const url = state?.token ? `${window.location.origin}/report/${state.token}` : "";
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Link copied"); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Share2 className="w-4 h-4" />Share report</DialogTitle></DialogHeader>
        {!state ? <div className="text-sm text-[color:var(--ink-3)] py-4 text-center">Loading…</div> : !state.enabled ? (
          <div className="space-y-3">
            <p className="text-sm text-[color:var(--ink-3)]">Anyone with the secure link can view this report with live data (run with your data permissions). You can add a password and an expiry date.</p>
            <Button data-testid="rd-share-enable" onClick={enable} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white w-full"><Link2 className="w-4 h-4 mr-1.5" />Create share link</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="rd-share-url" readOnly value={url} className="text-xs bg-[color:var(--surface-2)]" />
              <Button data-testid="rd-share-copy" variant="outline" onClick={copy} className="shrink-0 bg-[color:var(--surface-2)]"><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-2.5">
              <div className="text-[11px] font-semibold text-[color:var(--ink-2)] uppercase tracking-wide flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Security</div>
              <div className="flex items-center gap-2">
                <Input data-testid="rd-share-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder={state.password_protected ? "Password is set — type to replace" : "Optional password"} className="h-8 text-xs bg-[color:var(--surface-2)]" />
                <Button data-testid="rd-share-password-save" size="sm" variant="outline" onClick={savePw} disabled={busy || (!pw && !state.password_protected)} className="h-8 text-xs bg-[color:var(--surface-2)] shrink-0">
                  {pw ? "Set" : state.password_protected ? "Clear" : "Set"}
                </Button>
              </div>
              {state.password_protected && <div className="text-[10px] text-emerald-600">Password protection is ON</div>}
              <div className="flex items-center gap-2">
                <Input data-testid="rd-share-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-8 text-xs bg-[color:var(--surface-2)]" />
                <Button data-testid="rd-share-expiry-save" size="sm" variant="outline" onClick={saveExpiry} disabled={busy} className="h-8 text-xs bg-[color:var(--surface-2)] shrink-0">
                  {expiry ? "Set expiry" : state.expires_at ? "Clear" : "Set expiry"}
                </Button>
              </div>
              {state.expires_at && <div className="text-[10px] text-[color:var(--ink-3)]">Expires {new Date(state.expires_at).toLocaleDateString()}</div>}
            </div>
            <Button data-testid="rd-share-disable" variant="outline" onClick={disable} disabled={busy} className="w-full text-[color:var(--danger)] bg-[color:var(--surface-2)]">Turn off sharing</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ open, onOpenChange, reportId }) {
  const [schedules, setSchedules] = useState([]);
  const [recipients, setRecipients] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [format, setFormat] = useState("pdf");
  const [hour, setHour] = useState(8);
  const [weekday, setWeekday] = useState(0);
  const [perRls, setPerRls] = useState(false);
  const [threshold, setThreshold] = useState(0);
  const [bodyMode, setBodyMode] = useState("attachment");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/report-designs/${reportId}/schedule`).then(({ data }) => setSchedules(data)).catch(() => {}), [reportId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const create = async () => {
    const list = recipients.split(",").map((s) => s.trim()).filter(Boolean);
    if (!list.length) return toast.error("Add at least one recipient email");
    setBusy(true);
    try {
      await api.post(`/report-designs/${reportId}/schedule`, {
        recipients: list, frequency, format, hour_utc: Number(hour) || 8, weekday: Number(weekday) || 0,
        per_recipient_rls: perRls, alert_threshold_pct: Number(threshold) || 0,
        body_mode: bodyMode,
      });
      toast.success("Schedule created");
      setRecipients("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create schedule"); }
    setBusy(false);
  };
  const remove = async (sid) => { await api.delete(`/report-designs/${reportId}/schedule/${sid}`); load(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Mail className="w-4 h-4" />Email schedule</DialogTitle></DialogHeader>
        <Input data-testid="rd-sched-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="Recipients (comma separated emails)" className="bg-[color:var(--surface)]" />
        <div className="grid grid-cols-2 gap-2">
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger data-testid="rd-sched-frequency" className="h-9 text-sm bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
          </Select>
          <Select value={format} onValueChange={setFormat} disabled={bodyMode === "inline"}>
            <SelectTrigger data-testid="rd-sched-format" className="h-9 text-sm bg-[color:var(--surface)]"><SelectValue placeholder={bodyMode === "inline" ? "Inline HTML" : ""} /></SelectTrigger>
            <SelectContent>{FMT_LABELS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {frequency !== "hourly" && (
            <label className="text-xs text-[color:var(--ink-2)] flex items-center gap-2">Hour (UTC)
              <Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} className="h-8 w-16 text-xs bg-[color:var(--surface)]" />
            </label>
          )}
          {frequency === "weekly" && (
            <Select value={String(weekday)} onValueChange={setWeekday}>
              <SelectTrigger className="h-9 text-sm bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)] rounded-xl border border-[color:var(--glass-border)] px-3 py-2">
          <span className="pr-3">Personalize per recipient (row-level security)<br />
            <span className="text-[10px] text-[color:var(--ink-3)]">Recipients matching a user in your workspace get the report rendered with THEIR data permissions.</span></span>
          <Switch data-testid="rd-sched-perrls" checked={perRls} onCheckedChange={setPerRls} />
        </label>
        <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)] rounded-xl border border-[color:var(--glass-border)] px-3 py-2">
          <span className="pr-3">Embed report in email body<br />
            <span className="text-[10px] text-[color:var(--ink-3)]">Renders Matrix, nested-group tables and KPIs directly inline so recipients see the pivot without opening an attachment.</span></span>
          <Switch data-testid="rd-sched-inline" checked={bodyMode === "inline"} onCheckedChange={(v) => setBodyMode(v ? "inline" : "attachment")} />
        </label>
        <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)] rounded-xl border border-[color:var(--glass-border)] px-3 py-2">
          <span className="pr-3">Snapshot alert threshold<br />
            <span className="text-[10px] text-[color:var(--ink-3)]">Get pinged when any KPI moves ≥ this % between runs. 0 disables the alert.</span></span>
          <div className="flex items-center gap-1">
            <Input data-testid="rd-sched-threshold" type="number" min={0} max={500} step={1}
              value={threshold} onChange={(e) => setThreshold(e.target.value)}
              className="h-8 w-16 text-xs bg-[color:var(--surface)] text-right" />
            <span className="text-[10px] text-[color:var(--ink-3)]">%</span>
          </div>
        </label>
        {schedules.length > 0 && (
          <div className="space-y-1.5 border-t border-[color:var(--glass-border)] pt-2">
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-xs bg-[color:var(--surface-2)] rounded-lg px-2.5 py-1.5">
                <span className="truncate">{s.frequency} · {s.body_mode === "inline" ? "INLINE" : String(s.format).toUpperCase()} → {(s.recipients || []).join(", ")}
                  {s.body_mode === "inline" && <span className="ml-1.5 text-[color:var(--brand-mint)] font-medium">embedded body</span>}
                  {s.per_recipient_rls && <span className="ml-1.5 text-[color:var(--brand-ocean)] font-medium">per-user RLS</span>}
                  {s.last_status && <span className={`ml-1.5 ${s.last_status === "sent" ? "text-emerald-600" : "text-amber-600"}`}>({s.last_status})</span>}
                </span>
                <button data-testid={`rd-sched-del-${s.id}`} onClick={() => remove(s.id)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] ml-2"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button data-testid="rd-sched-create" onClick={create} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">Create schedule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// VersionsDialog was replaced by the right-side VersionTimeline pane (see components/bi/VersionTimeline.js).

function SaveTemplateDialog({ open, onOpenChange, reportId, defaultName }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(defaultName || ""); setDesc(""); } }, [open, defaultName]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/report-templates", { report_id: reportId, name: name || defaultName, description: desc });
      toast.success("Saved as team template");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><BookmarkPlus className="w-4 h-4" />Save as team template</DialogTitle></DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Teammates in your workspace will be able to start new reports from this template.</p>
        <Input data-testid="rd-tpl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" className="bg-[color:var(--surface)]" />
        <Input data-testid="rd-tpl-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Short description (optional)" className="bg-[color:var(--surface)]" />
        <DialogFooter><Button data-testid="rd-tpl-save-confirm" onClick={save} disabled={busy || !(name || defaultName)} className="bg-[color:var(--brand-ocean)] text-white">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save template
        </Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReportDesigner() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState({});
  const [running, setRunning] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const [tab, setTab] = useState("element");
  const [paramValues, setParamValues] = useState({});
  const [schedOpen, setSchedOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [versionPreview, setVersionPreview] = useState(null);   // version snapshot being previewed (read-only)
  const [compareWith, setCompareWith] = useState(null);         // secondary version for two-up compare
  const [exporting, setExporting] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const baselineRef = useRef(null);
  const reportRef = useRef(null);
  reportRef.current = report;
  const paramsRef = useRef({});
  paramsRef.current = paramValues;

  const savable = (r) => JSON.stringify(SAVE_FIELDS.reduce((o, k) => ({ ...o, [k]: r?.[k] }), {}));

  const runPreview = useCallback(async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/report-designs/${id}/run`, { params: paramsRef.current });
      setPreview(data.elements || {});
    } catch { /* keep last preview */ }
    setRunning(false);
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/report-designs/${id}`);
      baselineRef.current = savable(data);
      setReport(data);
      const defs = {};
      (data.parameters || []).forEach((p) => { if (p.name) defs[p.name] = p.default ?? ""; });
      setParamValues(defs);
      runPreview();
    } catch { toast.error("Report not found"); nav("/reports"); }
  }, [id, nav, runPreview]);

  useEffect(() => { load(); api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); }, [load]);

  // Comment count (badge on comments button)
  useEffect(() => {
    if (!id) return;
    api.get(`/report-designs/${id}/comments`).then(({ data }) => setCommentCount((data || []).length)).catch(() => {});
  }, [id, commentsOpen]);

  const save = useCallback(async () => {
    const r = reportRef.current;
    if (!r) return;
    setSaveState("saving");
    try {
      const body = SAVE_FIELDS.reduce((o, k) => ({ ...o, [k]: r[k] }), {});
      await api.put(`/report-designs/${id}`, body);
      baselineRef.current = savable(r);
      setSaveState("saved");
      // Refetch to pick up server-computed fields (branding, versions)
      try {
        const { data: fresh } = await api.get(`/report-designs/${id}`);
        setReport((prev) => ({ ...(prev || {}), branding: fresh.branding, versions: fresh.versions }));
      } catch { /* keep local state */ }
      runPreview();
    } catch { setSaveState("error"); toast.error("Save failed"); }
  }, [id, runPreview]);

  // debounced autosave — suspended while a historical version is being previewed
  const currentJson = report ? savable(report) : null;
  useEffect(() => {
    if (!report || baselineRef.current === null || currentJson === baselineRef.current) return;
    if (versionPreview) return;   // don't autosave while previewing history
    setSaveState("dirty");
    const t = setTimeout(save, 1200);
    return () => clearTimeout(t);
  }, [currentJson, report, save, versionPreview]);

  const patchReport = (p) => setReport((r) => ({ ...r, ...p }));
  const els = report?.elements || [];
  const setEls = (next) => patchReport({ elements: next });
  const selected = els.find((e) => e.id === selectedId);

  const addEl = (type) => {
    const el = { id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type, config: { ...(EL_DEFAULTS[type] || {}) } };
    setEls([...els, el]);
    setSelectedId(el.id);
    setTab("element");
  };
  const removeEl = (i) => { setEls(els.filter((_, j) => j !== i)); if (els[i]?.id === selectedId) setSelectedId(null); };
  const duplicateEl = (i) => {
    const copy = { ...els[i], id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, config: { ...(els[i].config || {}) } };
    setEls([...els.slice(0, i + 1), copy, ...els.slice(i + 1)]);
    setSelectedId(copy.id);
  };
  const dropOn = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...els];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setEls(next);
    setDragIdx(null);
    setOverIdx(null);
  };

  const exportFmt = async (fmt) => {
    setExporting(fmt);
    try {
      if (saveState !== "saved") await save();
      const { data } = await api.post(`/report-designs/${id}/render?fmt=${fmt}`, { params: paramValues }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(report.name || "report").replace(/\s+/g, "_")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    setExporting(null);
  };

  const tok = useMemo(() => tokFor(report, paramValues), [report, paramValues]);
  if (!report) return <div className="min-h-screen flex items-center justify-center text-sm text-[color:var(--ink-3)]" style={{ background: "var(--canvas)" }}><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading report…</div>;

  const pg = report.page || {};

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--canvas)", "--rd-primary": (report.branding?.primary_color) || report.theme?.primary || "#2B4EFF" }}>
      <header className="flex items-center gap-2 px-4 py-2 border-b border-[color:var(--glass-border)] bg-[color:var(--surface)] sticky top-0 z-20">
        <button data-testid="rd-back-btn" onClick={() => nav("/reports")} className="p-1.5 rounded-lg text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"><ArrowLeft className="w-4 h-4" /></button>
        <Input data-testid="rd-name-input" value={report.name || ""} onChange={(e) => patchReport({ name: e.target.value })}
          className="h-8 w-64 font-medium bg-transparent border-transparent hover:border-[color:var(--glass-border)] focus:border-[color:var(--brand-ocean)]" />
        <span data-testid="rd-save-state" className="text-[11px] text-[color:var(--ink-3)]">
          {saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved changes" : saveState === "error" ? "Save failed" : "Saved"}
        </span>
        <span className="flex-1" />
        <Button data-testid="rd-refresh-btn" variant="outline" size="sm" onClick={runPreview} disabled={running} className="h-8 text-xs bg-[color:var(--surface-2)]">
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Refresh data
        </Button>
        <Button data-testid="rd-history-btn" variant={histOpen ? "default" : "outline"} size="sm" onClick={() => setHistOpen((v) => !v)} className={`h-8 text-xs ${histOpen ? "bg-[color:var(--brand-ocean)] text-white" : "bg-[color:var(--surface-2)]"}`}><History className="w-3.5 h-3.5 mr-1" />History</Button>
        <Button data-testid="rd-comments-btn" variant="outline" size="sm" onClick={() => setCommentsOpen(true)} className="h-8 text-xs bg-[color:var(--surface-2)] relative">
          <MessagesSquare className="w-3.5 h-3.5 mr-1" />Comments
          {commentCount > 0 && <span data-testid="rd-comments-count" className="ml-1 min-w-[16px] h-4 px-1 rounded-full bg-[color:var(--brand-ocean)] text-white text-[9px] font-bold flex items-center justify-center">{commentCount}</span>}
        </Button>
        <Button data-testid="rd-save-tpl-btn" variant="outline" size="sm" onClick={() => setTplOpen(true)} className="h-8 text-xs bg-[color:var(--surface-2)]"><BookmarkPlus className="w-3.5 h-3.5 mr-1" />Save template</Button>
        <Button data-testid="rd-schedule-btn" variant="outline" size="sm" onClick={() => setSchedOpen(true)} className="h-8 text-xs bg-[color:var(--surface-2)]"><Clock className="w-3.5 h-3.5 mr-1" />Schedule</Button>
        <Button data-testid="rd-share-btn" variant="outline" size="sm" onClick={() => setShareOpen(true)} className="h-8 text-xs bg-[color:var(--surface-2)]"><Share2 className="w-3.5 h-3.5 mr-1" />Share</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="rd-export-btn" size="sm" className="h-8 text-xs bg-[color:var(--brand-ocean)] text-white">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {FMT_LABELS.map(([v, l]) => <DropdownMenuItem key={v} data-testid={`rd-export-${v}`} onClick={() => exportFmt(v)}>{l}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Left: element palette + outline */}
        <aside className="w-[230px] shrink-0 border-r border-[color:var(--glass-border)] bg-[color:var(--surface)] p-3 space-y-3 overflow-y-auto">
          <div>
            <div className="text-[11px] font-semibold text-[color:var(--ink-2)] uppercase tracking-wide mb-1.5">Add element</div>
            <div className="grid grid-cols-3 gap-1.5">
              {EL_TYPES.map(({ type, label, icon: Icon }) => (
                <button key={type} data-testid={`rd-add-${type}`} onClick={() => addEl(type)} title={label}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] py-2 px-1 text-[9.5px] text-[color:var(--ink-2)] hover:border-[color:var(--brand-ocean)] hover:text-[color:var(--brand-ocean)] transition-colors">
                  <Icon className="w-4 h-4" />{label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-[color:var(--ink-2)] uppercase tracking-wide mb-1.5">Outline <span className="normal-case font-normal text-[9px]">(drag to reorder)</span></div>
            {els.length === 0 && <div className="text-[11px] text-[color:var(--ink-3)]">Empty — add elements above.</div>}
            <div className="space-y-1">
              {els.map((el, i) => {
                const meta = EL_TYPES.find((t) => t.type === el.type) || EL_TYPES[0];
                const Icon = meta.icon;
                const title = (el.config?.text || "").slice(0, 22) || meta.label;
                return (
                  <div key={el.id} data-testid={`rd-outline-${i}`} onClick={() => { setSelectedId(el.id); setTab("element"); }}
                    draggable
                    onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
                    onDrop={(e) => { e.preventDefault(); dropOn(i); }}
                    onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                    className={`group flex items-center gap-1 rounded-lg px-1.5 py-1.5 text-xs cursor-pointer border transition-colors
                      ${selectedId === el.id ? "border-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/5 text-[color:var(--ink)]" : "border-transparent text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"}
                      ${dragIdx === i ? "opacity-40" : ""}
                      ${overIdx === i && dragIdx !== null && dragIdx !== i ? "border-t-2 border-t-[color:var(--brand-ocean)]" : ""}`}>
                    <span data-testid={`rd-el-drag-${i}`} className="cursor-grab active:cursor-grabbing text-[color:var(--ink-3)]"><GripVertical className="w-3.5 h-3.5" /></span>
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate flex-1">{title}</span>
                    <span className="hidden group-hover:flex items-center">
                      <button data-testid={`rd-el-dup-${i}`} onClick={(e) => { e.stopPropagation(); duplicateEl(i); }} className="p-0.5 hover:text-[color:var(--brand-ocean)]"><Copy className="w-3 h-3" /></button>
                      <button data-testid={`rd-el-del-${i}`} onClick={(e) => { e.stopPropagation(); removeEl(i); }} className="p-0.5 hover:text-[color:var(--danger)]"><Trash2 className="w-3 h-3" /></button>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Center: paper preview */}
        <main className="flex-1 overflow-auto p-6" onClick={() => setSelectedId(null)}>
          {(report.parameters || []).filter((p) => p.name).length > 0 && (
            <div data-testid="rd-params-bar" className="flex items-center gap-2 flex-wrap mb-4 max-w-[920px] mx-auto">
              {(report.parameters || []).filter((p) => p.name).map((p) => (
                <label key={p.id || p.name} className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)] glass-card pl-3 pr-1.5 py-1 rounded-xl">
                  {p.name}
                  <Input data-testid={`rd-param-${p.name}`} value={paramValues[p.name] ?? ""} onChange={(e) => setParamValues((v) => ({ ...v, [p.name]: e.target.value }))}
                    onClick={(e) => e.stopPropagation()} placeholder={p.default || "any"} className="h-7 w-32 text-xs bg-[color:var(--surface)]" />
                </label>
              ))}
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); runPreview(); }} className="h-7 text-xs bg-[color:var(--surface-2)]" data-testid="rd-params-apply">Apply</Button>
            </div>
          )}
          {compareWith ? (
            <div data-testid="rd-two-up" className="grid grid-cols-2 gap-4 max-w-[1600px] mx-auto">
              <div>
                <div className="text-[11px] font-semibold text-[color:var(--brand-ocean)] mb-1 text-center">A · previewing (older)</div>
                <ReportPaper report={{ ...report, ...versionPreview }}
                  tok={tok} preview={preview} emptyHint="Empty snapshot." />
              </div>
              <div>
                <div className="text-[11px] font-semibold text-[color:var(--ink-3)] mb-1 text-center">
                  B · {compareWith.label || `v${compareWith.index + 1}`}
                </div>
                <ReportPaper report={{ ...report, ...compareWith.data }}
                  tok={tok} preview={preview} emptyHint="Empty snapshot." />
              </div>
            </div>
          ) : (
            <ReportPaper report={versionPreview ? { ...report, ...versionPreview } : report}
              tok={tok} preview={preview} selectedId={versionPreview ? null : selectedId}
              onSelectEl={versionPreview ? undefined : (elId) => { setSelectedId(elId); setTab("element"); }}
              emptyHint="Add elements from the left panel to design your report." />
          )}
          <div className="text-center text-[10px] text-[color:var(--ink-3)] mt-3">
            {compareWith ? "Two-up compare · A is the previewed version, B is the compare target" : `${pg.size || "A4"} · ${pg.orientation || "portrait"} — exports paginate automatically`}
          </div>
        </main>

        {/* Right: settings OR version timeline */}
        {histOpen ? (
          <VersionTimeline open={histOpen} onOpenChange={setHistOpen} reportId={id}
            liveDraft={report}
            onPreviewChange={setVersionPreview}
            onCompareChange={setCompareWith}
            onRestored={load} />
        ) : (
          <aside className="w-[290px] shrink-0 border-l border-[color:var(--glass-border)] bg-[color:var(--surface)] flex flex-col">
          <div className="flex border-b border-[color:var(--glass-border)]">
            {[["element", "Element"], ["page", "Page"], ["params", "Parameters"]].map(([k, l]) => (
              <button key={k} data-testid={`rd-tab-${k}`} onClick={() => setTab(k)}
                className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${tab === k ? "border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]" : "border-transparent text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]"}`}>{l}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {tab === "element" && (selected ? (
              <ElementSettings key={selected.id} el={selected} datasets={datasets}
                patch={(p) => setEls(els.map((e) => (e.id === selected.id ? { ...e, ...p, config: p.config || e.config } : e)))} />
            ) : (
              <div className="text-xs text-[color:var(--ink-3)] pt-6 text-center">Select an element in the outline or preview to edit its settings.</div>
            ))}
            {tab === "page" && <PageSettings report={report} patch={patchReport} />}
            {tab === "params" && (
              <>
                <ParametersEditor parameters={report.parameters || []} onChange={(p) => patchReport({ parameters: p })} datasets={datasets} />
                <ReportFiltersEditor filters={report.filters || []} onChange={(f) => patchReport({ filters: f })} datasets={datasets} />
              </>
            )}
          </div>
          </aside>
        )}
      </div>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} reportId={id} />
      <ScheduleDialog open={schedOpen} onOpenChange={setSchedOpen} reportId={id} />
      <SaveTemplateDialog open={tplOpen} onOpenChange={setTplOpen} reportId={id} defaultName={report.name} />
      <ReportCommentsPanel open={commentsOpen} onOpenChange={setCommentsOpen} reportId={id} currentUserId={user?.id} onCountChange={setCommentCount} />
    </div>
  );
}
